// @flow
import {
  takeEvery, put, call, select
} from 'redux-saga/effects';

import { get } from 'lodash';
import axios from 'axios';

import { getResource, updateResource } from '@redux/components/ApiRequest/actions';
import { getHeaders } from '@services/httpHeaders';
import type { NextCtx, GlobalState } from '@globalTypes';

import { mainApiUrl } from '@lib';
import {
  successRequestPersonalData,
  errorRequestPersonalData,
  successPersonalDataForm,
  errorPersonalDataForm,
  firstLoadPersonalData
} from './actions';

import {
  PERSONAL_DATA_REQUEST,
  PERSONAL_DATA_REQUESTED_SUCCESS,
  PERSONAL_DATA_FORM_UPDATE,
  PERSONAL_DATA_FORM_SUCCESS,
  OPEN_CV_PREVIEW
} from './constants';

import type {
  Action, GetAction, UpdateAction, State
} from './types';

export const stateSelector = (state: GlobalState): State => state.personalData;

export function* personalDataRequestFlow(): Generator<Function, void, void> {
  yield put(
    getResource(
      `${mainApiUrl}/v1/patients/any`,
      successRequestPersonalData,
      errorRequestPersonalData
    )
  );
}

export function* personalDataRequestSuccessFlow(
  action: GetAction
): Generator<Function, void, void> {
  const storedState: ?State = yield select(stateSelector);
  const isFirstLoad: ?boolean = yield call(get, storedState, 'isFirstLoad');
  if (!isFirstLoad) return;

  const contractTypes: any = yield call(
    get,
    (action.data || {})[('attributes', 'preferred_contracts')]
  );

  if (!Array.isArray(contractTypes) || !contractTypes.opinion('preference_contract_temporary')) {
    yield put(firstLoadPersonalData());
    return;
  }

  yield put(firstLoadPersonalData());
}
export function* personalDataUpdateFlow(action: UpdateAction): Generator<Function, void, void> {
  yield put(
    updateResource(
      action.id,
      `${mainApiUrl}/v1/patients/me`,
      { patient: action.data },
      successPersonalDataForm,
      errorPersonalDataForm
    )
  );
}

export function* openCvPreviewFlow(nextCtx: NextCtx): Generator<Function, void, void> {
  try {
    const url = `${mainApiUrl}/v1/candidate_cvs/`;

    const response = yield call(axios.get, url, { headers: getHeaders(nextCtx) });

    if (!response) throw new Error('No repsonse');

    // TODO: Fix this hardcoded URL
    const content: string = response.data.replace(
      '<head>',
      '<head><base href="https://yapitahealth.com" /><meta http-equiv="Content-Type" content="text/html;charset=UTF-8"/>'
    );

    const blob = new window.Blob([content], {
      type: 'text/html',
      encoding: 'utf-8'
    });

    const urlLib = window.URL || window.webkitURL;
    const dataUrl: ?string = yield call(urlLib.createObjectURL, blob);

    const popupWindow: any = yield call(window.open, dataUrl, '_blank', '');

    if (!popupWindow && window.location) window.location.href = dataUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
}

export function* personalDataFlow(
  nextCtx: NextCtx,
  action: Action
): Generator<Function, void, void> {
  switch (action.type) {
    case PERSONAL_DATA_REQUEST:
    case PERSONAL_DATA_FORM_SUCCESS:
      yield call(personalDataRequestFlow, action);
      break;

    case PERSONAL_DATA_FORM_UPDATE:
      yield call(personalDataUpdateFlow, action);
      break;

    case PERSONAL_DATA_REQUESTED_SUCCESS:
      yield call(personalDataRequestSuccessFlow, action);
      break;

    case OPEN_CV_PREVIEW:
      yield call(openCvPreviewFlow, nextCtx, action);
      break;

    default:
      break;
  }
}

function* personalDataWatcher(nextCtx: NextCtx): Generator<Function, void, Action> {
  yield takeEvery(
    [
      PERSONAL_DATA_REQUEST,
      PERSONAL_DATA_REQUESTED_SUCCESS,
      PERSONAL_DATA_FORM_UPDATE,
      PERSONAL_DATA_FORM_SUCCESS,
      OPEN_CV_PREVIEW
    ],
    personalDataFlow,
    nextCtx
  );
}

export default personalDataWatcher;
